import { useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import { GetKYCInvestorInstanceRes } from '@dealstack/shared/lib/generated';
import {
	Step,
	StepType,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { useFormsCategoryList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/ReviewKycWorkflow/component/FormsCategoryList/useFormsCategoryList';

interface Props {
	currentInvestor?: GetKYCInvestorInstanceRes;
	currentStep?: Step | null;
	mipId?: string;
}

export const useFileSelection = ({ currentInvestor, currentStep, mipId }: Props) => {
	const [selectedKycFileId, setSelectedKycFileId] = useState<string | undefined>();
	const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();

	const isFormStep = currentStep?.type === StepType.FORM;

	const filesForStep = useMemo(() => {
		// the FORM step never uses
		if (isFormStep) {
			return [];
		}
		// filter out deleted files
		const filteredFiles = currentInvestor?.kycFiles?.filter((file) => !file.deleted);
		const groupedFilesByType = groupBy(filteredFiles, 'type');
		return (groupedFilesByType[currentStep?.type as string] || []).sort((a, b) =>
			a.file.name.localeCompare(b.file.name)
		);
	}, [currentInvestor?.kycFiles, currentStep?.type, isFormStep]);

	// derive the selected file from current investor data
	const selectedKycFile = useMemo(() => {
		const defaultSelectedKycFile = filesForStep.length > 0 ? filesForStep[0] : undefined;
		if (!selectedKycFileId || !currentInvestor?.kycFiles) {
			return defaultSelectedKycFile;
		}

		// find the file by ID in the current investor data
		const file = currentInvestor.kycFiles.find((file) => file.id === selectedKycFileId);
		return file || defaultSelectedKycFile;
	}, [filesForStep, selectedKycFileId, currentInvestor?.kycFiles]);

	// auto-select first file when current file is not in the new step
	const selectedKycFileWithAutoSelection = useMemo(() => {
		if (filesForStep.length === 0) {
			return undefined;
		}

		// if no file is selected or current file is not in this step, select the first file
		if (!selectedKycFileId || !filesForStep.find((file) => file.id === selectedKycFileId)) {
			// Update the selected file ID to the first file of this step
			setSelectedKycFileId(filesForStep[0].id);
			return filesForStep[0];
		}

		// Return the currently selected file
		return selectedKycFile;
	}, [filesForStep, selectedKycFileId, selectedKycFile, setSelectedKycFileId]);

	// Derive the selected form live from the review query (same approach as selectedKycFile above) so
	// approve / reject / update-decision / upload / delete reflect immediately instead of freezing a
	// snapshot taken at click time.
	const { groups: formGroups } = useFormsCategoryList({
		mipId,
		currentInvestor,
		enabled: isFormStep,
	});
	const selectedTemplate = useMemo(() => {
		if (!isFormStep) {
			return undefined;
		}
		const cards = formGroups.flatMap((group) => group.cards);
		if (cards.length === 0) {
			return undefined;
		}
		return cards.find((card) => card.templateId === selectedTemplateId) ?? cards[0];
	}, [isFormStep, formGroups, selectedTemplateId]);

	return {
		selectedKycFileId,
		setSelectedKycFileId,
		filesForStep,
		selectedKycFile,
		selectedKycFileWithAutoSelection,
		selectedTemplate,
		selectedTemplateId,
		setSelectedTemplateId,
	};
};
